import { type PermissionAuditEntry, PERMISSIONS, isPermission } from '#shared/types';

/* An audit entry's JSON diff, turned into a sentence a person can read. */

interface Diff {
  before?: Change | Exceptions;
  after?: Change | Exceptions;
  added?: string[];
  removed?: string[];
}

function label(key: string): string {
  return isPermission(key) ? `${PERMISSIONS[key].module} · ${key}` : key;
}

function list(keys: readonly string[]): string {
  const shown = keys.slice(0, 4).map(label).join(', ');
  return keys.length > 4 ? `${shown} and ${keys.length - 4} more` : shown;
}

type Change = { name?: string; permissions?: string[] };
type Exceptions = { permission: string; effect: string }[];

/** One line a person can read, rather than the raw JSON diff. */
export function describePermissionChange(
  entry: Pick<PermissionAuditEntry, 'action' | 'diff'>,
): string {
  const diff = (entry.diff ?? {}) as Diff;
  if (entry.action === 'created') return 'Created the role.';
  if (entry.action === 'deleted') return 'Deleted the role.';
  if (entry.action === 'updated') return describeUpdate(diff);
  if (entry.action === 'overrides')
    return describeExceptions(Array.isArray(diff.after) ? diff.after : []);
  return entry.action;
}

function describeUpdate(diff: Diff): string {
  const before = (diff.before as Change | undefined)?.name;
  const after = (diff.after as Change | undefined)?.name;
  const parts = [
    before && after && before !== after ? `Renamed “${before}” to “${after}”.` : '',
    diff.added?.length ? `Granted ${list(diff.added)}.` : '',
    diff.removed?.length ? `Removed ${list(diff.removed)}.` : '',
  ].filter(Boolean);
  return parts.join(' ') || 'Saved with no permission changes.';
}

function describeExceptions(after: Exceptions): string {
  if (after.length === 0) return 'Cleared every exception; back to the role’s permissions.';
  const allowed = after
    .filter((override) => override.effect === 'allow')
    .map((override) => override.permission);
  const denied = after
    .filter((override) => override.effect === 'deny')
    .map((override) => override.permission);
  return [
    allowed.length ? `Allowed ${list(allowed)}.` : '',
    denied.length ? `Denied ${list(denied)}.` : '',
  ]
    .filter(Boolean)
    .join(' ');
}
